import ITravelPassSlideData from '@app/types/data/slides/ITravelPassSlideData';
import { SeasonCalendar } from '@app/services/export/ppt/classes/SeasonCalendar';
import { NumberNavigation, INumberNavigationItem } from '@app/services/export/ppt/classes/NumberNavigation';
import { PriceTable } from '@app/services/export/ppt/classes/PriceTable';
import { defaultViewPortPadding, presentationDimensions } from '@app/services/export/ppt/variables/constants';
import { gridColWidth } from '@app/services/export/ppt/utils/gridUtils';
import IPriceTableRow from '@app/types/data/base/IPriceTableRow';
import { ConnectingTicket } from '@app/services/export/ppt/classes/ConnectingTicket';
import PptxGenJS from 'pptxgenjs';
import { Map } from '@app/services/export/ppt/classes/Map';
import AbstractOfferSlideProcessor, {
	IPptOfferSlideOptions
} from '@app/services/export/ppt/slides/AbstractOfferSlideProcessor';
import PowerPointPresentation from '@app/services/export/ppt/PowerPointPresentation';

export default class PowerPointTravelPassSlideProcessor extends AbstractOfferSlideProcessor<ITravelPassSlideData> {
	protected masterName = this.slideData.id;

	public constructor(slideData: ITravelPassSlideData, presentation: PowerPointPresentation) {
		const options: Partial<IPptOfferSlideOptions> = {
			imageWidth: defaultViewPortPadding.horizontal + gridColWidth(6)
		};

		super(slideData, presentation, options);
	}

	public async processSlideData(): Promise<void> {
		if (this.slideData.image) {
			await this.adjustImageDimensions(this.slideData.image);
		}

		if (this.slideData.image) {
			this.presentation.defineSlideMaster({
				title: this.masterName,
				objects: Map.createMapMasterObjectsForImage(
					this.slideData.image,
					this.options.imageWidth,
					presentationDimensions.h
				)
			});
		}

		this.slideData.priceMatrix.forEach((item, index) => {
			const visibleSvgGroup = item.svgGroupId || 'default';
			this.updateSlideData({ visibleSvgGroup });
			const { x, y, w } = this.getAvailableSpaceRectangle();
			const offerSlideBase: PptxGenJS.Slide = this.createOfferSlideBase();

			const dayNavigation = new NumberNavigation(
				offerSlideBase,
				{
					links: this.getDayNavigationData(),
					currentIndex: index,
					singular: this.i18n.day,
					plural: this.i18n.days
				},
				{ x, y, w }
			);

			const priceTable = new PriceTable(
				offerSlideBase,
				{
					rows: this.getTableRows(index),
					rowLabelWidth: 0.65
				},
				{ x, y: dayNavigation.getDimensionRect().bottom + 0.2, w }
			);

			const connectingTicket = new ConnectingTicket(
				offerSlideBase,
				{
					connection: this.slideData.connection,
					seasons: this.slideData.connection.seasons
				},
				{ x, y: priceTable.getDimensionRect().bottom + 0.18, w }
			);

			dayNavigation.create();
			priceTable.create();
			connectingTicket.create();

			const {seasons} = this.slideData.connection;
			if (seasons && seasons.length > 1) {
				const seasonCalendar = new SeasonCalendar(offerSlideBase, { seasons }, { x, w });
				seasonCalendar.create();
			}
		});
	}

	private getTableRows(index: number): IPriceTableRow[] {
		return [
			{
				label: this.i18n.b2c,
				individuals: this.slideData.priceMatrix[index].b2c
			},
			{
				label: this.i18n.b2b,
				groups: this.slideData.priceMatrix[index].b2b
			}
		];
	}

	private getDayNavigationData(): INumberNavigationItem[] {
		return this.slideData.priceMatrix.map((item, index) => ({
			numberLabel: item.dayCount,
			slideNumberLink: this.presentation.getSlideNumberById(this.slideData.id) + index
		}));
	}

	protected getSlideCount(): number {
		return this.slideData.priceMatrix.length;
	}
}
